<script setup lang="ts">
/**
 * Round social icons from the template's footer and article share block.
 *
 * `profiles` lists only the accounts that actually exist (the same two the
 * Organization JSON-LD claims); `share` renders share intents for a URL instead.
 */
const props = withDefaults(defineProps<{
  variant?: 'light' | 'dark'
  /** When set, the buttons share this URL instead of linking to our profiles. */
  share?: { url: string; title: string }
}>(), {
  variant: 'light',
})

const { t } = useI18n()

const profiles = [
  { id: 'linkedin', icon: 'bi-linkedin', href: 'https://www.linkedin.com/company/inmyresume', label: 'LinkedIn' },
  { id: 'x', icon: 'bi-twitter-x', href: 'https://x.com/inmyresume', label: 'X' },
]

const shareLinks = computed(() => {
  if (!props.share) return []
  const url = encodeURIComponent(props.share.url)
  const text = encodeURIComponent(props.share.title)
  return [
    { id: 'x', icon: 'bi-twitter-x', href: `https://twitter.com/intent/tweet?url=${url}&text=${text}`, label: 'X' },
    { id: 'facebook', icon: 'bi-facebook', href: `https://www.facebook.com/sharer/sharer.php?u=${url}`, label: 'Facebook' },
    { id: 'linkedin', icon: 'bi-linkedin', href: `https://www.linkedin.com/sharing/share-offsite/?url=${url}`, label: 'LinkedIn' },
    { id: 'mail', icon: 'bi-envelope', href: `mailto:?subject=${text}&body=${url}`, label: 'E-mail' },
  ]
})

const links = computed(() => (props.share ? shareLinks.value : profiles))
</script>

<template>
  <div class="flex gap-4">
    <a
      v-for="link in links"
      :key="link.id"
      :href="link.href"
      target="_blank"
      rel="noopener noreferrer"
      class="social-link"
      :class="{ 'social-link-dark': variant === 'dark' }"
      :aria-label="share ? t('sofax.share.on', { network: link.label }) : link.label"
      :title="share ? t('sofax.share.on', { network: link.label }) : link.label"
    >
      <i :class="['bi', link.icon]" />
    </a>
  </div>
</template>
